import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, X, Shield, Award, Car, Star, Bot, Calendar, MessageCircle, Clock, Users, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { smoothScrollTo } from '../lib/utils';

const PricingV2: React.FC = () => {
  const plans = [
    {
      name: 'GRIT SUITE',
      icon: <Car size={24} />,
      price: 97,
      originalPrice: 147,
      description: 'Complete technology platform for managing your detailing business operations efficiently',
      badge: 'Technology Platform',
      features: {
        platform: {
          "Advanced scheduling with SMS reminders": true,
          "Transcribe All Calls": true,
          "Unified Inbox For All Platforms": true,
          "Marketing automation": true,
          "Integrates with all major shop software": true,
          "Landing Page Builder": true,
          "Workflow Builder": true,
          "Advanced reporting": true,
          "Staff management": true
        },
        coaching: {
          "Group coaching sessions": false,
          "Weekly office hours": false,
          "Members-only Discord community": false,
          "Systems optimization training": false,
          "Staff training resources": false,
          "Real business growth strategies": false,
          "SOP templates & frameworks": false,
          "Peer accountability groups": false, 
          "Monthly group workshops": false,
          "Access to recorded trainings": false
        },
        marketing: {
          "Custom landing page development": false,
          "Email marketing campaigns": false,
          "Text message campaigns": false,
          "Promotional planning": false,
          "Platform configuration & setup": false,
          "Training on platform usage": false,
          "Platform troubleshooting & support": false,
          "Priority tech support": false
        }
      },
      extraServices: {
        "Social media management": "+$500/mo",
        "Video editing services": "+$500/mo",
        "CHiP AI Assistant": "+$347/mo (Reg. $497)"
      },
      ctaText: "Start Free Trial",
      popular: false
    },
    {
      name: 'DetailerOS',
      icon: <Users size={24} />,
      price: 297,
      originalPrice: 347,
      description: 'Group coaching and powerful technology to transform your detailing business and mindset',
      badge: 'Coaching + Technology',
      features: {
        platform: {
          "Advanced scheduling with SMS reminders": true,
          "Transcribe All Calls": true,
          "Unified Inbox For All Platforms": true,
          "Marketing automation": true,
          "Integrates with all major shop software": true,
          "Landing Page Builder": true,
          "Workflow Builder": true,
          "Advanced reporting": true,
          "Staff management": true
        },
        coaching: {
          "Group coaching sessions": true,
          "Weekly office hours": true,
          "Members-only Discord community": true,
          "Systems optimization training": true,
          "Staff training resources": true,
          "Real business growth strategies": true,
          "SOP templates & frameworks": true,
          "Peer accountability groups": true, 
          "Monthly group workshops": true,
          "Access to recorded trainings": true
        },
        marketing: {
          "Custom landing page development": false,
          "Email marketing campaigns": false,
          "Text message campaigns": false,
          "Promotional planning": false,
          "Platform configuration & setup": false,
          "Training on platform usage": false,
          "Platform troubleshooting & support": false,
          "Priority tech support": false
        }
      },
      extraServices: {
        "Social media management": "+$500/mo",
        "Video editing services": "+$500/mo",
        "CHiP AI Assistant": "+$347/mo (Reg. $497)"
      },
      ctaText: "Apply Now",
      popular: true
    },
    {
      name: 'DONE FOR YOU',
      icon: <Award size={24} />,
      price: 647,
      originalPrice: 697,
      description: 'Full service package including coaching, technology, and done-for-you marketing execution',
      badge: 'Complete Solution',
      features: {
        platform: {
          "Advanced scheduling with SMS reminders": true,
          "Transcribe All Calls": true,
          "Unified Inbox For All Platforms": true,
          "Marketing automation": true,
          "Integrates with all major shop software": true,
          "Landing Page Builder": true,
          "Workflow Builder": true,
          "Advanced reporting": true,
          "Staff management": true
        },
        coaching: {
          "Group coaching sessions": true,
          "Weekly office hours": true,
          "Members-only Discord community": true,
          "Systems optimization training": true,
          "Staff training resources": true,
          "Real business growth strategies": true,
          "SOP templates & frameworks": true,
          "Peer accountability groups": true, 
          "Monthly group workshops": true,
          "Access to recorded trainings": true
        },
        marketing: {
          "Custom landing page development": true,
          "Email marketing campaigns": true,
          "Text message campaigns": true,
          "Promotional planning": true,
          "Platform configuration & setup": true,
          "Training on platform usage": true,
          "Platform troubleshooting & support": true,
          "Priority tech support": true
        }
      },
      extraServices: {
        "Social media management": "+$500/mo",
        "Video editing services": "+$500/mo",
        "CHiP AI Assistant": "+$347/mo (Reg. $497)"
      },
      ctaText: "Contact Us",
      popular: false
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1.0] }
    }
  };

  // State for mobile plan selection
  const [activeMobilePlan, setActiveMobilePlan] = useState(1); // Default to the popular plan
  
  // State to control feature list visibility on mobile
  const [showFeatureList, setShowFeatureList] = useState(false);

  // Toggle feature list visibility
  const toggleFeatureList = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowFeatureList(!showFeatureList);
  };

  return (
    <section id="pricing" className="py-20 relative overflow-hidden">
      {/* Background gradient */}
      <motion.div
        className="gradient-overlay w-[600px] h-[600px] bottom-[-300px] left-0"
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 0.4, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.5, ease: "easeOut" }}
      />

      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-10 max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <motion.span
            className="px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent font-semibold text-sm inline-block mb-4"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Pricing Options
          </motion.span>
          <h2 className="text-3xl md:text-4xl font-extrabold mb-6">Select the Right Path for Your Detailing Business</h2>
          <p className="text-white/70 text-lg mb-4">Find the perfect plan that aligns with your business goals and growth strategy.</p>
          <div className="inline-block px-4 py-2 bg-accent/20 text-accent font-bold rounded-full mb-4 animate-pulse">
            🚀 Launch Pricing - Save $50 on all plans! Limited Time Offer
          </div>
        </motion.div>

        {/* Mobile Plan Selector Tabs - Visible only on small screens */}
        <div className="md:hidden mb-6">
          <div className="flex rounded-lg overflow-hidden bg-white/5 border border-white/10">
            {plans.map((plan, index) => (
              <button
                key={`mobile-tab-${index}`}
                className={`flex-1 py-3 text-sm font-medium text-center transition-colors ${
                  activeMobilePlan === index
                    ? 'bg-accent text-background'
                    : 'bg-transparent hover:bg-white/10'
                }`}
                onClick={() => setActiveMobilePlan(index)}
              >
                {plan.name}
              </button>
            ))}
          </div>
        </div>

        {/* Mobile Plan Display - Only visible on mobile */}
        <div className="md:hidden mb-8">
          {plans.map((plan, index) => (
            <div
              key={`mobile-plan-${index}`}
              className={`${activeMobilePlan === index ? 'block' : 'hidden'}`}
            >
              <div className={`glassmorphic rounded-xl p-6 relative ${plan.popular ? 'border-accent shadow-glow-sm' : 'border-white/10'}`}>
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-accent text-background text-xs font-bold px-3 py-1 rounded-full">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                
                <div className="flex flex-col items-center text-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mb-2">
                    {plan.icon}
                  </div>
                  <h3 className="text-xl font-bold">{plan.name}</h3>
                  <p className="text-xs text-white/70 mb-1">{plan.badge}</p>
                  <div className="text-3xl font-bold mt-2">${plan.price}<span className="text-lg font-normal">/mo</span></div>
                  <div className="text-sm text-white/60 line-through">${plan.originalPrice}/mo</div>
                  <div className="text-xs text-yellow-400 font-semibold">Launch Pricing - Save $50!</div>
                </div>
                
                <p className="text-center text-sm text-white/70 mb-6">{plan.description}</p>
                
                <motion.button
                  className={`w-full py-3 rounded-lg font-semibold mb-6 flex items-center justify-center gap-2 ${
                    plan.popular ? 'bg-accent text-background hover:bg-accent-light' : 'bg-white/10 hover:bg-white/20'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span>{plan.ctaText}</span>
                  <ChevronRight size={16} />
                </motion.button>
                
                {/* Feature summary for mobile */}
                <div className="space-y-3 mb-4">
                  <div className="bg-white/5 p-3 rounded-lg">
                    <h4 className="font-semibold mb-2 text-accent">Technology Platform</h4>
                    <p className="text-sm">{Object.values(plan.features.platform).filter(Boolean).length} features included</p>
                  </div>
                  
                  <div className="bg-white/5 p-3 rounded-lg">
                    <h4 className="font-semibold mb-2 text-accent">DetailerOS</h4>
                    <p className="text-sm">{Object.values(plan.features.coaching).filter(Boolean).length} features included</p>
                  </div>
                  
                  <div className="bg-white/5 p-3 rounded-lg">
                    <h4 className="font-semibold mb-2 text-accent">Done For You Backend Marketing Team</h4>
                    <p className="text-sm">{Object.values(plan.features.marketing).filter(Boolean).length} features included</p>
                  </div>
                </div>
                
                <div className="text-center text-sm">
                  <button
                    onClick={toggleFeatureList}
                    className="text-accent hover:underline flex items-center justify-center mx-auto"
                  >
                    {showFeatureList ? (
                      <>Hide full feature list <ChevronUp size={16} className="ml-1" /></>
                    ) : (
                      <>View full feature list <ChevronDown size={16} className="ml-1" /></>
                    )}
                  </button>
                </div>

                {/* Full feature list for mobile - shown/hidden based on state */}
                {showFeatureList && (
                  <motion.div 
                    className="mt-6 border-t border-white/10 pt-6"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="space-y-6">
                      {/* Technology Platform Features */}
                      <div>
                        <h4 className="text-lg font-bold text-accent mb-3">Technology Platform</h4>
                        <div className="space-y-2">
                          {Object.entries(plan.features.platform).map(([feature, included], idx) => (
                            <div key={`mobile-platform-${idx}`} className={`flex items-center gap-2 p-2 ${idx % 2 === 0 ? 'bg-white/5' : ''} rounded-lg`}>
                              {included ? (
                                <Check className="h-4 w-4 text-accent flex-shrink-0" />
                              ) : (
                                <X className="h-4 w-4 text-white/30 flex-shrink-0" />
                              )}
                              <span className="text-sm">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* DetailerOS Features */}
                      <div>
                        <h4 className="text-lg font-bold text-accent mb-3">DetailerOS</h4>
                        <div className="space-y-2">
                          {Object.entries(plan.features.coaching).map(([feature, included], idx) => (
                            <div key={`mobile-coaching-${idx}`} className={`flex items-center gap-2 p-2 ${idx % 2 === 0 ? 'bg-white/5' : ''} rounded-lg`}>
                              {included ? (
                                <Check className="h-4 w-4 text-accent flex-shrink-0" />
                              ) : (
                                <X className="h-4 w-4 text-white/30 flex-shrink-0" />
                              )}
                              <span className="text-sm">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Marketing Features */}
                      <div>
                        <h4 className="text-lg font-bold text-accent mb-3">Done For You Backend Marketing</h4>
                        <div className="space-y-2">
                          {Object.entries(plan.features.marketing).map(([feature, included], idx) => (
                            <div key={`mobile-marketing-${idx}`} className={`flex items-center gap-2 p-2 ${idx % 2 === 0 ? 'bg-white/5' : ''} rounded-lg`}>
                              {included ? (
                                <Check className="h-4 w-4 text-accent flex-shrink-0" />
                              ) : (
                                <X className="h-4 w-4 text-white/30 flex-shrink-0" />
                              )}
                              <span className="text-sm">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Additional Services */}
                      <div>
                        <h4 className="text-lg font-bold text-accent mb-3">Additional Services</h4>
                        <div className="space-y-2">
                          {Object.entries(plan.extraServices).map(([service, price], idx) => (
                            <div 
                              key={`mobile-extra-${idx}`} 
                              className={`flex items-center justify-between p-2 ${idx % 2 === 0 ? 'bg-white/5' : ''} rounded-lg ${service === "CHiP AI Assistant" ? "bg-accent/10 border border-accent/30" : ""}`}
                            >
                              <span className={`text-sm ${service === "CHiP AI Assistant" ? "font-semibold" : ""}`}>
                                {service === "CHiP AI Assistant" && <Bot className="inline-block mr-1 text-accent" size={14} />}
                                {service}
                              </span>
                              <span className={`text-sm ${service === "CHiP AI Assistant" ? "text-yellow-400 font-semibold" : "text-white/70"}`}>
                                {price}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 text-center">
                      <button
                        onClick={toggleFeatureList}
                        className="text-accent hover:underline flex items-center justify-center mx-auto"
                      >
                        Hide full feature list <ChevronUp size={16} className="ml-1" />
                      </button>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Pricing Table - Only visible on desktop */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="hidden md:block glassmorphic rounded-xl p-4 md:p-8 max-w-6xl mx-auto overflow-hidden"
        >
          {/* Plans header */}
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="col-span-1"></div>
            {plans.map((plan, index) => (
              <motion.div
                key={`plan-header-${index}`}
                variants={itemVariants}
                className={`col-span-1 text-center relative ${plan.popular ? 'z-10' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-0 right-0 text-center">
                    <span className="bg-accent text-background text-xs font-bold px-3 py-1 rounded-full">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                <div className={`rounded-lg p-4 h-full flex flex-col justify-between ${plan.popular ? 'bg-accent/10 border-2 border-accent' : 'bg-white/5 border border-white/10'}`}>
                  <div>
                    <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-3">
                      {plan.icon}
                    </div>
                    <h3 className="text-xl font-bold mb-1">{plan.name}</h3>
                    <p className="text-xs text-white/70 mb-3">{plan.badge}</p>
                    <div className="text-3xl font-bold mb-1">${plan.price}<span className="text-lg font-normal">/mo</span></div>
                    <div className="text-sm text-white/60 line-through mb-1">${plan.originalPrice}/mo</div>
                    <div className="text-xs text-yellow-400 font-semibold">Launch Pricing - Save $50!</div>
                  </div>
                  <motion.button
                    className={`mt-4 py-2 px-4 rounded-lg font-semibold w-full flex items-center justify-center gap-2 ${plan.popular ? 'bg-accent text-background hover:bg-accent-light' : 'bg-white/10 hover:bg-white/20'}`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span>{plan.ctaText}</span>
                    <ChevronRight size={16} />
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Feature comparison table with horizontal scrolling for smaller screens */}
          <div className="overflow-x-auto pb-4">
            <div className="min-w-[768px]"> {/* Set minimum width to ensure table is scrollable on smaller screens */}
              {/* Technology Platform Features */}
              <div className="mb-8">
                <div className="grid grid-cols-4 gap-4 mb-2">
                  <div className="col-span-1">
                    <h3 className="text-lg font-bold text-accent">Technology Platform</h3>
                  </div>
                  <div className="col-span-3"></div>
                </div>
                
                {Object.keys(plans[0].features.platform).map((feature, index) => (
                  <div 
                    key={`platform-${index}`} 
                    className={`grid grid-cols-4 gap-4 ${index % 2 === 0 ? 'bg-white/5' : ''} p-3 rounded-lg mb-1`}
                  >
                    <div className="col-span-1">{feature}</div>
                    {plans.map((plan, planIndex) => (
                      <div key={`platform-${index}-${planIndex}`} className="col-span-1 text-center">
                        {plan.features.platform[feature] ? (
                          <Check className="h-5 w-5 text-accent mx-auto" />
                        ) : (
                          <X className="h-5 w-5 text-white/30 mx-auto" />
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              {/* DetailerOS Features */}
              <div className="mb-8">
                <div className="grid grid-cols-4 gap-4 mb-2">
                  <div className="col-span-1">
                    <h3 className="text-lg font-bold text-accent">DetailerOS</h3>
                  </div>
                  <div className="col-span-3"></div>
                </div>
                
                {Object.keys(plans[0].features.coaching).map((feature, index) => (
                  <div 
                    key={`coaching-${index}`} 
                    className={`grid grid-cols-4 gap-4 ${index % 2 === 0 ? 'bg-white/5' : ''} p-3 rounded-lg mb-1`}
                  >
                    <div className="col-span-1">{feature}</div>
                    {plans.map((plan, planIndex) => (
                      <div key={`coaching-${index}-${planIndex}`} className="col-span-1 text-center">
                        {plan.features.coaching[feature] ? (
                          <Check className="h-5 w-5 text-accent mx-auto" />
                        ) : (
                          <X className="h-5 w-5 text-white/30 mx-auto" />
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              {/* Done For You Features */}
              <div className="mb-8">
                <div className="grid grid-cols-4 gap-4 mb-2">
                  <div className="col-span-1">
                    <h3 className="text-lg font-bold text-accent">Done For You Backend Marketing Team</h3>
                  </div>
                  <div className="col-span-3"></div>
                </div>
                
                {Object.keys(plans[0].features.marketing).map((feature, index) => (
                  <div 
                    key={`marketing-${index}`} 
                    className={`grid grid-cols-4 gap-4 ${index % 2 === 0 ? 'bg-white/5' : ''} p-3 rounded-lg mb-1`}
                  >
                    <div className="col-span-1">{feature}</div>
                    {plans.map((plan, planIndex) => (
                      <div key={`marketing-${index}-${planIndex}`} className="col-span-1 text-center">
                        {plan.features.marketing[feature] ? (
                          <Check className="h-5 w-5 text-accent mx-auto" />
                        ) : (
                          <X className="h-5 w-5 text-white/30 mx-auto" />
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              {/* Additional/Extra Services */}
              <div className="mb-8">
                <div className="grid grid-cols-4 gap-4 mb-2">
                  <div className="col-span-1">
                    <h3 className="text-lg font-bold text-accent">Additional Services</h3>
                  </div>
                  <div className="col-span-3"></div>
                </div>
                
                {Object.keys(plans[0].extraServices).map((service, index) => (
                  <div 
                    key={`extra-${index}`} 
                    className={`grid grid-cols-4 gap-4 ${index % 2 === 0 ? 'bg-white/5' : ''} p-3 rounded-lg mb-1 ${service === "CHiP AI Assistant" ? "bg-accent/10 border border-accent/30" : ""}`}
                  >
                    <div className={`col-span-1 ${service === "CHiP AI Assistant" ? "font-bold" : ""}`}>
                      {service === "CHiP AI Assistant" && <Bot className="inline-block mr-2 text-accent" size={16} />}
                      {service}
                    </div>
                    {plans.map((plan, planIndex) => (
                      <div key={`extra-${index}-${planIndex}`} className="col-span-1 text-center">
                        <span className={`text-sm ${service === "CHiP AI Assistant" ? "text-yellow-400 font-semibold" : plan.extraServices[service].includes('+') ? "text-white/70" : "text-white/70"}`}>
                          {plan.extraServices[service]}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <div className="bg-accent/10 border border-accent/30 rounded-lg p-4 mb-6 inline-block">
              <p className="text-accent font-semibold">🔥 Limited Time Launch Pricing - Save $50/month on all plans!</p>
            </div>
            <p className="text-white/70 mb-4">Need a custom solution? Looking for more information?</p>
            <motion.button 
              className="btn btn-primary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Schedule a Free Consultation
            </motion.button>
          </div>
        </motion.div>

        {/* CHiP AI Assistant Highlight */}
        <motion.div 
          className="glassmorphic rounded-xl p-8 mt-12 border-accent/50 shadow-glow-sm max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.3 }}
        >
          <div className="flex flex-col md:flex-row gap-8 items-center">
            <div className="md:w-1/2">
              <div className="bg-accent/10 p-3 rounded-full inline-flex items-center justify-center mb-4">
                <Bot size={28} className="text-accent" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold mb-3">CHiP AI Assistant</h3>
              <h4 className="text-xl text-white/90 mb-4">The High End Auto Detailing Industry's Only AI Assistant</h4>
              <div className="mb-6 space-y-3">
                <div className="flex items-start gap-2">
                  <Calendar className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <p>Books appointments 24/7 without human intervention</p>
                </div>
                <div className="flex items-start gap-2">
                  <MessageCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <p>Sets up consultations and handles complex sales objections</p>
                </div>
                <div className="flex items-start gap-2">
                  <Clock className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <p>Never sleeps, never takes a day off - works around the clock</p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-accent/10 p-3 rounded-lg border border-accent/30 mb-6">
                <Star className="w-5 h-5 text-accent flex-shrink-0" />
                <p className="font-semibold">
                  <span className="text-accent text-xl">$347/month</span>
                  <span className="text-white/60 line-through ml-2">$497/month</span>
                  <span className="text-yellow-400 text-sm block">Launch pricing - Save $150/month!</span>
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <motion.button
                  className="btn btn-primary flex-1 flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <span>Add CHiP to Your Plan</span>
                </motion.button>
                <Link to="/chip">
                  <motion.button
                    className="btn btn-secondary flex-1 flex items-center justify-center gap-2"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <span>Learn More</span>
                    <ChevronRight size={18} />
                  </motion.button>
                </Link>
              </div>
            </div>
            <div className="md:w-1/2">
              <motion.div 
                className="glassmorphic rounded-xl p-6 border-white/10 relative overflow-hidden"
                animate={{ 
                  boxShadow: ["0 0 15px rgba(204, 255, 0, 0.3)", "0 0 30px rgba(204, 255, 0, 0.5)", "0 0 15px rgba(204, 255, 0, 0.3)"]
                }}
                transition={{ 
                  duration: 3, 
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <div className="flex items-center justify-between mb-4 border-b border-white/10 pb-3">
                  <div className="flex items-center gap-2">
                    <Bot className="w-6 h-6 text-accent" />
                    <span className="font-bold">CHiP AI Assistant</span>
                  </div>
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                    <div className="text-xs text-white/70">Online 24/7</div>
                  </div>
                </div>
                
                <div className="space-y-3 mb-4">
                  <div className="flex items-start gap-3">
                    <div className="bg-white/10 rounded-full p-2 flex-shrink-0">
                      <Users size={14} className="text-white/70" />
                    </div>
                    <div className="bg-white/10 rounded-lg p-3 text-sm">
                      Hi, I'd like to book a ceramic coating for my Tesla Model 3.
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 flex-row-reverse">
                    <div className="bg-accent rounded-full p-2 flex-shrink-0">
                      <Bot size={14} className="text-background" />
                    </div>
                    <div className="bg-accent/10 border border-accent/20 rounded-lg p-3 text-sm">
                      I'd be happy to help you book a ceramic coating for your Tesla Model 3! We offer several coating packages. Would you prefer our Standard (2-year), Pro (5-year), or Elite (lifetime) protection?
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="bg-white/10 rounded-full p-2 flex-shrink-0">
                      <Users size={14} className="text-white/70" />
                    </div>
                    <div className="bg-white/10 rounded-lg p-3 text-sm">
                      What's the price difference between them?
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 flex-row-reverse">
                    <div className="bg-accent rounded-full p-2 flex-shrink-0">
                      <Bot size={14} className="text-background" />
                    </div>
                    <div className="bg-accent/10 border border-accent/20 rounded-lg p-3 text-sm">
                      Great question! For a Tesla Model 3: Standard is $899, Pro is $1,499, and Elite is $1,999. The Elite package includes annual inspections and maintenance for life. Would you like to see the detailed breakdown of what each package includes?
                    </div>
                  </div>
                </div>
                
                <div className="text-center text-xs text-white/50">
                  CHiP handles inquiries, objections, and bookings automatically
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingV2;